import React, { forwardRef, createRef, useEffect, useMemo, Children } from 'react';
import { Fade, Slide } from '@wds/motion';
import { createMemoStyles, safeRest, KEYS, defaultFonts, usePreviousState, uniqueId } from '@wds/utils';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { IconX } from '@wds/icons';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

var SIZES = ['xs', 'sm', 'md', 'lg', 'xl']; /// ///////////////////////// PALETTE START ////////////////////////////

var SEMANTIC_COLORS = ['primary', 'secondary', 'success', 'warning', 'upgrade', 'error', 'info', 'muted'];
var TYPE_COLORS = ['dark', 'light', 'darkMuted', 'lightMuted', 'link'];
var BACKGROUND_COLORS = ['main', 'light', 'dark', 'overlay', 'accent', 'primary', 'success', 'warning', 'error', 'info', 'upgrade', 'muted'];
var BORDER_COLORS = ['main', 'active', 'hover', 'muted'];
var DATA_COLORS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']; /// ///////////////////////// FONT TYPE START ////////////////////////////

var FONT_WEIGHTS = ['light', 'regular', 'medium'];

var dataTransform = function dataTransform(arr) {
  return Object.freeze(arr.map(function (x) {
    return "data-".concat(x);
  }));
};

var DATA_VIS_COLORS = dataTransform(DATA_COLORS);

var getDataColor = function getDataColor(s) {
  return s.split('-')[1];
}; /// ///////////////////////// HELPERS ////////////////////////////


var readOnlyIncludes = function readOnlyIncludes(arr, el) {
  return arr.includes(el);
}; /// ///////////////////////// TYPE GUARDS ////////////////////////////


var isColorShape = function isColorShape(c) {
  return _typeof(c) === 'object' && 'main' in c;
};

var isSemanticColor = function isSemanticColor(c) {
  return readOnlyIncludes(SEMANTIC_COLORS, c);
};

var isLinkColor = function isLinkColor(c) {
  return c === 'link';
};

var isTypeColor = function isTypeColor(c) {
  return readOnlyIncludes(TYPE_COLORS, c);
};

var isBackgroundColor = function isBackgroundColor(c) {
  return readOnlyIncludes(BACKGROUND_COLORS, c);
};

var isBorderColor = function isBorderColor(c) {
  return readOnlyIncludes(BORDER_COLORS, c);
};

var isDataColor = function isDataColor(c) {
  return readOnlyIncludes(DATA_COLORS, c);
};

var isDataVisColor = function isDataVisColor(c) {
  return readOnlyIncludes(DATA_VIS_COLORS, c);
};

var getColor = function getColor(palette, color, _a) {
  var _b = _a === void 0 ? {} : _a,
      _c = _b.semanticKey,
      semanticKey = _c === void 0 ? 'main' : _c,
      _d = _b.colorType,
      colorType = _d === void 0 ? 'semantic' : _d,
      fallback = _b.fallback;

  var c = fallback !== null && fallback !== void 0 ? fallback : 'currentcolor';
  if (!color) return c;
  var cType = colorType; // if passed a portion of the Pallette that is the semantic color use that.

  if (isColorShape(color)) c = color[semanticKey];else {
    // Check Semantic
    if (cType === 'semantic') {
      if (isSemanticColor(color)) c = palette[color][semanticKey];else cType = 'link';
    } // Check type and get Color


    if (cType === 'link') {
      if (isLinkColor(color)) c = palette[color][semanticKey];else cType = 'text';
    }

    if (cType === 'text') {
      if (isTypeColor(color)) c = palette[cType][color];else cType = 'background';
    }

    if (cType === 'background') {
      if (isBackgroundColor(color)) c = palette[cType][color];else cType = 'border';
    }

    if (cType === 'border') {
      if (isBorderColor(color)) c = palette[cType][color];else cType = 'data';
    }

    if (cType === 'data') {
      if (isDataVisColor(color)) c = palette[cType][getDataColor(color)];
      if (isDataColor(color)) c = palette[cType][color];
    }
  }
  return c;
};

process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var palette$1 = {
  transparent: 'transparent',
  primary: {
    main: '#00BF6F',
    contrast: '#FFFFFF'
  },
  secondary: {
    main: '#6B787F',
    contrast: '#FFFFFF'
  },
  success: {
    main: '#00BF6F',
    contrast: '#FFFFFF'
  },
  warning: {
    main: '#F05B24',
    contrast: '#FFFFFF'
  },
  error: {
    main: '#F05B24',
    contrast: '#FFFFFF'
  },
  info: {
    main: '#2DCCD3',
    contrast: '#333E48'
  },
  upgrade: {
    main: '#F9BE00',
    contrast: '#333E48'
  },
  muted: {
    main: '#D0D2D3',
    contrast: '#333E48'
  },
  link: {
    main: '#007FAA',
    contrast: '#FFFFFF'
  },
  text: {
    dark: '#333E48',
    light: '#FFFFFF',
    darkMuted: '#6B787F',
    lightMuted: '#D0D2D3',
    link: '#007FAA'
  },
  background: {
    main: '#F7F8FA',
    light: '#FFFFFF',
    dark: '#333E48',
    overlay: 'rgba(107, 120, 127, .86)',
    accent: '#EDEEEE',
    primary: '#E5F9F1',
    success: '#E5F9F1',
    warning: '#FDEBE5',
    error: '#F8ECF2',
    info: '#E6F9FA',
    upgrade: '#FEF2CC',
    muted: '#EDEEEE'
  },
  border: {
    main: '#D0D2D3',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#00BF6F',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
({
  main: [1, 'solid', getColor(palette$1, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette$1, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette$1, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette$1, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette$1, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette$1, 'light', {
    colorType: 'background'
  })]
});
process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var palette = {
  transparent: 'transparent',
  primary: {
    main: '#008323',
    contrast: '#FFFFFF'
  },
  secondary: {
    main: '#757575',
    contrast: '#FFFFFF'
  },
  success: {
    main: '#008323',
    contrast: '#FFFFFF'
  },
  warning: {
    main: '#F7B500',
    contrast: '#404040'
  },
  error: {
    main: '#CC0000',
    contrast: '#FFFFFF'
  },
  info: {
    main: '#2cb2f1',
    contrast: '#000000'
  },
  upgrade: {
    main: '#ffe11b',
    contrast: '#404040'
  },
  muted: {
    main: '#d0d0d0',
    contrast: '#404040'
  },
  link: {
    main: '#0a7cc1',
    contrast: '#FFFFFF'
  },
  text: {
    dark: '#404040',
    light: '#FFFFFF',
    darkMuted: '#757575',
    lightMuted: '#efefef',
    link: '#0a7cc1'
  },
  background: {
    main: '#f7f7f7',
    light: '#ffffff',
    dark: '#404040',
    overlay: 'rgba(117, 117, 117, .86)',
    accent: '#efefef',
    primary: '#E5F3E9',
    success: '#E5F3E9',
    warning: '#FFEEC0',
    error: '#FBEAEA',
    info: '#DCF0FF',
    upgrade: '#FFF9D1',
    muted: '#EFEFEF'
  },
  border: {
    main: '#d0d0d0',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#008323',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
getColor(palette, 'main', {
  colorType: 'border'
});
({
  main: [1, 'solid', getColor(palette, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette, 'light', {
    colorType: 'background'
  })]
});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

createMemoStyles(function (_a) {
  var _b = _a.type,
      assets = _b.assets,
      fontFamily = _b.fontFamily,
      fontWeight = _b.fontWeight,
      fontSize = _b.fontSize;
  var fontPath = "".concat(assets.base).concat(assets.path);
  var fontFaces = FONT_WEIGHTS.map(function (name) {
    // we need this because the font file names are all National2-<Weight>.2.ext
    var upperCaseName = "".concat(name[0].toUpperCase()).concat(name.slice(1));
    var weight = fontWeight[name];
    return {
      fontFamily: assets.name,
      fontWeight: weight,
      fontDisplay: 'swap',
      src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff2) format('woff2')"),
      fallbacks: [{
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff) format('woff')")
      }, {
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".eot#iefix) format('embedded-opentype')")
      }]
    };
  });
  return {
    '@font-face': fontFaces,
    '@global': {
      "[class*='wrench'], [class*='wrench']::before, [class*='wrench']::after": {
        fontFamily: fontFamily.base,
        '-webkit-font-smoothing': 'antialiased',
        fontSize: fontSize.body,
        boxSizing: 'border-box',
        padding: 0,
        margin: 0,
        listStyle: 'none'
      }
    }
  };
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var useStyles$4 = createMemoStyles(function (_ref) {
  var spacing = _ref.spacing,
      type = _ref.type;
  return {
    wrenchCloseButton: function wrenchCloseButton(_ref2) {
      var component = _ref2.component;
      var componentSpecificStyles = {
        badge: {
          position: 'relative',
          top: 'none',
          right: 'none',
          padding: 0,
          marginLeft: spacing[3]
        },
        banner: {
          position: 'absolute',
          top: spacing[2],
          right: spacing[2],
          padding: 0
        },
        alert: {
          padding: [spacing[2], 0, spacing[2], spacing[5]],
          textAlign: 'right'
        },
        default: {
          position: 'absolute',
          right: spacing[1],
          top: spacing[1],
          padding: spacing[2]
        }
      };
      return _objectSpread(_objectSpread({}, componentSpecificStyles[component || 'default']), {}, {
        fontSize: type.fontSize.body,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '0',
        textDecoration: 'none',
        background: 'transparent',
        border: 'none',
        '&:hover': {
          transition: 'none',
          textDecoration: 'none'
        },
        '&:active': {
          transition: 'none',
          outline: 'none'
        }
      });
    }
  };
});
var useStyles$5 = useStyles$4;
var _excluded = ["children", "component", "label", "onClick", "type", "variant"];

var CloseButton = function CloseButton(_ref) {
  _ref.children;
  var component = _ref.component,
      label = _ref.label,
      onClick = _ref.onClick;
  _ref.type;

  var variant = _ref.variant,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();

  var _useStyles = useStyles$5({
    theme: theme,
    component: component
  }),
      wrenchCloseButton = _useStyles.wrenchCloseButton;

  return /*#__PURE__*/React.createElement("button", Object.assign({
    onClick: onClick,
    type: "button",
    className: wrenchCloseButton,
    "aria-label": label
  }, safeRest(rest)), /*#__PURE__*/React.createElement(IconX, {
    title: "close",
    color: variant
  }));
};

CloseButton.displayName = "CloseButton";
CloseButton.propTypes = {
  children: PropTypes.string,
  component: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  variant: PropTypes.oneOf(['dark', 'light'])
};
CloseButton.defaultProps = {
  children: 'CloseButton',
  component: undefined,
  type: undefined,
  variant: 'light'
};
var CloseButton$1 = CloseButton;
/* eslint-disable jsx-a11y/no-static-element-interactions */

var Overlay = function Overlay(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  return (
    /*#__PURE__*/
    // eslint-disable-next-line testing-library/consistent-data-testid
    React.createElement("div", {
      className: className,
      onClick: onClick,
      "data-testid": "wds-overlay"
    })
  );
};

Overlay.displayName = "Overlay";
Overlay.propTypes = {
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func
};
Overlay.defaultProps = {
  onClick: undefined
};
var Overlay$1 = Overlay;
var useStyles$3 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchModalBody: function (_a) {
      var padding = _a.padding;
      return __assign({
        overflowY: 'auto',
        flexGrow: 1
      }, padding && {
        padding: [spacing[4], spacing[6], spacing[6], spacing[6]]
      });
    }
  };
});
var ModalBody = forwardRef(function (_a, ref) {
  _a.className;

  var children = _a.children,
      handleClick = _a.handleClick,
      id = _a.id,
      padding = _a.padding,
      rest = __rest(_a, ["className", "children", "handleClick", "id", "padding"]);

  var wrenchModalBody = useStyles$3({
    padding: padding
  }).wrenchModalBody;

  var handleKeyDown = function (e) {
    if (e.key === KEYS.ESC) {
      handleClick === null || handleClick === void 0 ? void 0 : handleClick(e);
      e.preventDefault();
    }
  };

  return (// eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("section", __assign({
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: 0,
      ref: ref,
      id: id,
      className: wrenchModalBody,
      onKeyDown: handleKeyDown
    }, safeRest(rest)), children)
  );
});
ModalBody.displayName = 'ModalBody';
ModalBody.defaultProps = {
  className: undefined,
  handleClick: undefined,
  id: undefined,
  padding: true
};
var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
      spacing = theme.spacing;
  return {
    wrenchModalFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      flexShrink: 0,
      padding: spacing[6],
      background: palette.background.light,
      borderTop: "1px solid ".concat(palette.background.accent),
      color: palette.text.dark
    },
    wrenchModalActions: function (_a) {
      var align = _a.align;
      return __assign(__assign(__assign({
        display: 'flex',
        flexGrow: 1,
        flexWrap: 'wrap'
      }, align === 'right' && {
        justifyContent: 'flex-end'
      }), align === 'left' && {
        justifyContent: 'flex-start'
      }), {
        '&:not([class^="wrenchButtonGroup"]) > button': {
          marginLeft: spacing[2]
        }
      });
    }
  };
});

var ModalFooter = function (_a) {
  var align = _a.align,
      children = _a.children;

  var _b = useStyles$2({
    align: align
  }),
      wrenchModalActions = _b.wrenchModalActions,
      wrenchModalFooter = _b.wrenchModalFooter;

  return React.createElement("footer", {
    className: wrenchModalFooter
  }, React.createElement("div", {
    className: wrenchModalActions
  }, children));
};

ModalFooter.defaultProps = {
  align: 'left'
};
var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette,
      spacing = theme.spacing,
      _a = theme.type,
      fontWeight = _a.fontWeight,
      fontSize = _a.fontSize;
  return {
    wrenchModalHeader: function (_a) {
      var stretched = _a.stretched;
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: [spacing[6], spacing[6], spacing[2], spacing[6]],
        background: palette.background.light,
        flexShrink: 0,
        flexDirection: stretched ? 'column' : 'row'
      };
    },
    wrenchModalText: {
      fontSize: 24,
      fontWeight: fontWeight.regular,
      color: palette.text.dark,
      marginTop: 0,
      marginBottom: spacing[2]
    },
    wrenchModalTitle: {
      flexGrow: 1,
      width: '100%'
    },
    wrenchModalSubText: {
      fontSize: fontSize.body,
      fontWeight: fontWeight.regular,
      color: palette.text.dark,
      margin: [spacing[1], 0, 0]
    },
    wrenchModalAddOn: {
      flexGrow: 1,
      marginTop: spacing[3],
      width: '100%',
      minWidth: '33%',
      textAlign: 'right',
      // In the case that an addon is the first child (modal title was omitted)
      // we do not want a margin top
      '& :first-child': {
        marginTop: 0
      }
    }
  };
});

var ModalHeader = function (_a) {
  var addOn = _a.addOn,
      header = _a.header,
      id = _a.id,
      subHeader = _a.subHeader,
      stretched = _a.stretched;

  if (subHeader && addOn) {
    throw new Error('You cannot have both subHeader and addon');
  } else if (!header && subHeader) {
    throw new Error('You must provide a header if you provide a subHeader');
  }

  var _b = useStyles$1({
    stretched: stretched
  }),
      wrenchModalAddOn = _b.wrenchModalAddOn,
      wrenchModalHeader = _b.wrenchModalHeader,
      wrenchModalSubText = _b.wrenchModalSubText,
      wrenchModalText = _b.wrenchModalText,
      wrenchModalTitle = _b.wrenchModalTitle;

  return React.createElement("section", {
    className: wrenchModalHeader
  }, React.createElement("div", {
    className: wrenchModalTitle
  }, header && React.createElement("h2", {
    className: wrenchModalText,
    id: id
  }, header), subHeader && React.createElement("p", {
    className: wrenchModalSubText
  }, subHeader)), addOn && React.createElement("div", {
    className: wrenchModalAddOn
  }, addOn));
};

ModalHeader.defaultProps = {
  addOn: undefined,
  header: undefined,
  id: undefined,
  stretched: false,
  subHeader: undefined
};
var modalWidth = {
  sm: '443px',
  md: '676px',
  lg: '909px',
  xl: '1300px',
  full: '100%'
};
var useStyles = createMemoStyles(function (theme) {
  var _a;

  var breakpoints = theme.breakpoints,
      palette = theme.palette,
      shadow = theme.shadow,
      spacing = theme.spacing;
  return {
    wrenchModal: function (_a) {
      var elevation = _a.elevation;
      return {
        overflow: 'auto',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'none',
        zIndex: elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space
      };
    },
    wrenchModalContainer: __assign(__assign({}, defaultFonts(theme)), (_a = {
      boxSizing: 'border-box',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      color: palette.text.dark,
      background: palette.text.light,
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return (elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space) + 1;
      },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      whiteSpace: 'normal',
      outline: 'none',
      pointerEvents: 'all'
    }, _a["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
      maxWidth: function (_a) {
        var size = _a.size;
        return {
          sm: '80vw',
          md: '80vw',
          lg: '80vw',
          xl: '96vw',
          full: '100vw'
        }[size];
      },
      maxHeight: function (_a) {
        var size = _a.size;
        return {
          sm: '90vh',
          md: '90vh',
          lg: '90vh',
          xl: '96vw',
          full: '100vw'
        }[size];
      },
      borderRadius: function (_a) {
        var fullPage = _a.fullPage;
        return fullPage ? 0 : spacing[4];
      },
      boxShadow: shadow.space,
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      height: function (_a) {
        var fullPage = _a.fullPage;
        return fullPage ? '100%' : 'auto';
      },
      width: function (_a) {
        var size = _a.size;
        return modalWidth[size];
      }
    }, _a)),
    wrenchModalOverlayContainer: function (_a) {
      var elevation = _a.elevation;
      return {
        zIndex: elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space,
        position: 'relative'
      };
    },
    wrenchModalOverlay: {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      position: 'fixed',
      backgroundColor: palette.background.overlay
    }
  };
});

__spreadArray(__spreadArray([], SIZES.filter(function (x) {
  return x !== 'xs';
}), true), ['full'], false);

var Modal = function (_a) {
  var children = _a.children,
      closeButtonLabel = _a.closeButtonLabel,
      dismissible = _a.dismissible,
      onClose = _a.onClose,
      show = _a.show,
      _b = _a.size,
      size = _b === void 0 ? 'sm' : _b,
      title = _a.title,
      elevation = _a.elevation,
      role = _a.role,
      rest = __rest(_a, ["children", "closeButtonLabel", "dismissible", "onClose", "show", "size", "title", "elevation", "role"]);

  var modalBodyRef = createRef();
  var prevShow = usePreviousState(show);
  var fullPage = size === 'full';

  var _c = useStyles({
    size: size,
    elevation: elevation,
    fullPage: fullPage
  }),
      wrenchModal = _c.wrenchModal,
      wrenchModalContainer = _c.wrenchModalContainer,
      wrenchModalOverlay = _c.wrenchModalOverlay,
      wrenchModalOverlayContainer = _c.wrenchModalOverlayContainer;

  useEffect(function () {
    if (modalBodyRef.current && prevShow !== show) {
      modalBodyRef.current.focus();
    }

    if (typeof document !== 'undefined') {
      var parent_1 = document.getElementsByTagName('html')[0];

      if (parent_1) {
        if (show) {
          parent_1.style.overflow = 'hidden';
        } else {
          parent_1.style.overflow = 'auto';
        }
      }
    }
  }, [modalBodyRef, prevShow, show]);
  var titleId = useMemo(function () {
    return uniqueId('modal-title-');
  }, []);
  var bodyId = useMemo(function () {
    return uniqueId('modal-body-');
  }, []);

  var handleClick = function () {
    if (dismissible) {
      if (onClose) {
        onClose();
      }
    }
  };
  /**
   * ModalHeader exists show aria-labelledby
   * @type {boolean}
   */


  var hasModalHeader = Children.toArray(children).some(function (child) {
    return React.isValidElement(child) && child.type === ModalHeader;
  });
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: fullPage ? '' : wrenchModalOverlayContainer
  }, React.createElement(Fade, {
    in: show
  }, React.createElement("div", null, React.createElement(Overlay$1, {
    onClick: handleClick,
    className: fullPage ? '' : wrenchModalOverlay
  })))), React.createElement(Slide, {
    in: show,
    direction: "down",
    distance: "near",
    speed: "fast"
  }, React.createElement("div", null, React.createElement("div", __assign({
    className: wrenchModal
  }, safeRest(rest)), React.createElement("div", {
    className: wrenchModalContainer,
    "aria-label": title,
    "aria-labelledby": hasModalHeader ? titleId : undefined,
    "aria-describedby": bodyId,
    role: role,
    "aria-modal": "true",
    tabIndex: -1
  }, dismissible && React.createElement(CloseButton$1, {
    variant: "dark",
    onClick: handleClick,
    label: closeButtonLabel
  }), Children.map(children, function (child) {
    if (React.isValidElement(child)) {
      var additionalProps = void 0;

      if (child.type === ModalHeader) {
        additionalProps = {
          id: titleId
        };
      } else if (child.type === ModalBody) {
        additionalProps = {
          id: bodyId,
          handleClick: handleClick,
          ref: modalBodyRef
        };
      }

      if (additionalProps) {
        return React.cloneElement(child, additionalProps);
      }
    }

    return child;
  }))))));
};

Modal.defaultProps = {
  closeButtonLabel: 'Close Modal',
  dismissible: true,
  onClose: undefined,
  show: false,
  size: 'sm',
  title: undefined,
  elevation: undefined,
  role: 'dialog'
};
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;
var MODAL_SIZES = ['sm', 'md', 'lg', 'xl', 'full'];
export { MODAL_SIZES, Modal };